import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TechWatchDigest, TechWatchItem } from '../services/techWatchService';
import { getDigest, listEpisodes, generateDigest, reviewDigest, requestStt, getSttStatus, getHtmlReport, selectItems } from '../services/techWatchService';

interface TechWatchDigestPanelProps {
  watchId: string;
  onClose: () => void;
  onEditWatch?: () => void;
}

export function TechWatchDigestPanel({ watchId, onClose, onEditWatch }: TechWatchDigestPanelProps) {
  const { t } = useTranslation();
  const [digest, setDigest] = useState<TechWatchDigest | null>(null);
  const [items, setItems] = useState<TechWatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedForStt, setSelectedForStt] = useState<Set<string>>(new Set());
  const [selectedForKb, setSelectedForKb] = useState<Set<string>>(new Set());
  const [acquiring, setAcquiring] = useState(false);
  const [reportHtml, setReportHtml] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, eRes] = await Promise.all([
        getDigest(watchId).catch(() => null),
        listEpisodes(watchId).catch(() => ({ data: [] })),
      ]);
      setDigest(dRes?.data ?? null);
      setItems(eRes.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [watchId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateDigest(watchId);
      setTimeout(load, 3000);
    } finally {
      setGenerating(false);
    }
  };

  const handleReview = async (status: 'reviewed' | 'dismissed') => {
    if (!digest) return;
    await reviewDigest(watchId, digest.id, status);
    load();
  };

  const handleStt = async () => {
    if (selectedForStt.size === 0) return;
    const ids = Array.from(selectedForStt);
    await requestStt(watchId, ids);
    setSelectedForStt(new Set());
    setTimeout(async () => {
      for (const eid of ids) {
        try {
          const res = await getSttStatus(watchId, eid);
          setItems(prev => prev.map(ep =>
            ep.id === eid ? { ...ep, stt_status: res.data.stt_status } : ep
          ));
        } catch { /* ignore */ }
      }
    }, 5000);
  };

  const handleAcquire = async () => {
    if (selectedForKb.size === 0) return;
    setAcquiring(true);
    try {
      await selectItems(watchId, Array.from(selectedForKb));
      setSelectedForKb(new Set());
      // Mark items as downloading optimistically
      setItems(prev => prev.map(it =>
        selectedForKb.has(it.id) ? { ...it, user_selected: true, full_text_status: 'downloading' } : it
      ));
      setTimeout(load, 5000);
    } finally {
      setAcquiring(false);
    }
  };

  const toggleStt = (id: string) => {
    setSelectedForStt(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleKb = (id: string) => {
    setSelectedForKb(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const handleViewReport = async () => {
    if (reportHtml) { setReportHtml(null); return; }
    setReportLoading(true);
    try {
      const res = await getHtmlReport(watchId);
      setReportHtml(res.data as unknown as string);
    } catch {
      alert('Failed to generate report.');
    } finally {
      setReportLoading(false);
    }
  };

  const relevanceColor = (score: number) => {
    if (score >= 4) return '#16a34a';
    if (score >= 2) return '#ca8a04';
    return '#9ca3af';
  };

  const sourceTypeBadge = (type: string) => (
    <span style={{
      fontSize: 10,
      background: type === 'arxiv' ? '#ede9fe' : type === 'openalex' ? '#e0f2fe' : '#f0fdf4',
      color: type === 'arxiv' ? '#7c3aed' : type === 'openalex' ? '#0369a1' : '#15803d',
      padding: '1px 5px', borderRadius: 3, marginLeft: 4
    }}>
      {type === 'openalex' ? 'OpenAlex' : type}
    </span>
  );

  const fullTextIcon = (status: string) => {
    if (status === 'downloaded') return <span style={{ color: '#16a34a', fontSize: 12 }}>&#10003;</span>;
    if (status === 'downloading') return <span style={{ color: '#ca8a04', fontSize: 11 }}>&#8987;</span>;
    if (status === 'failed') return <span style={{ color: '#dc2626', fontSize: 11 }}>!</span>;
    return null;
  };

  if (loading) return <div style={{ padding: 16 }}>Loading...</div>;

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{t('techWatch.digestTitle')}</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {onEditWatch && (
            <button onClick={onEditWatch} className="action-btn"
              style={{ fontSize: 12, padding: '3px 10px', background: '#6c757d', color: '#fff' }}>
              {t('techWatch.editWatch')}
            </button>
          )}
          <button onClick={handleViewReport} disabled={reportLoading} className="action-btn"
            style={{ fontSize: 12, padding: '3px 10px', background: '#7c3aed', color: '#fff' }}>
            {reportLoading ? '...' : reportHtml ? t('techWatch.hideReport') : t('techWatch.viewReport')}
          </button>
          <button onClick={handleGenerate} disabled={generating} className="action-btn"
            style={{ fontSize: 12, padding: '3px 10px', background: '#2563eb', color: '#fff' }}>
            {generating ? t('techWatch.generating') : t('techWatch.refreshDigest')}
          </button>
          {selectedForKb.size > 0 && (
            <button onClick={handleAcquire} disabled={acquiring} className="action-btn"
              style={{ fontSize: 12, padding: '3px 10px', background: '#0891b2', color: '#fff' }}>
              {acquiring ? t('techWatch.acquiring') : `${t('techWatch.acquireSelected')} (${selectedForKb.size})`}
            </button>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>&times;</button>
        </div>
      </div>

      {reportHtml && (
        <div style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <iframe srcDoc={reportHtml} style={{ width: '100%', height: '70vh', border: 'none' }}
            title="Tech Watch report" sandbox="allow-same-origin" />
        </div>
      )}

      {digest && (
        <div style={{ marginBottom: 16 }}>
          <details open style={{ background: '#f9fafb', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>&#9660;</span>
              {t('techWatch.overallSummary')}
            </summary>
            <div style={{ marginTop: 6, lineHeight: 1.6 }}>
              {digest.digest_text || digest.overall_summary}
            </div>
          </details>
          {digest.status === 'pending_review' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleReview('reviewed')} className="action-btn"
                style={{ fontSize: 12, padding: '3px 10px', background: '#16a34a', color: '#fff', border: 'none' }}>
                {t('techWatch.markReviewed')}
              </button>
              <button onClick={() => handleReview('dismissed')} className="action-btn"
                style={{ fontSize: 12, padding: '3px 10px' }}>
                {t('techWatch.dismiss')}
              </button>
            </div>
          )}
        </div>
      )}

      {!digest && items.length === 0 && (
        <div style={{ color: '#6b7280' }}>{t('techWatch.noEpisodes')}</div>
      )}

      {items.length > 0 && (() => {
        const top20 = [...items]
          .sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0))
          .slice(0, 20);
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontWeight: 600 }}>{t('techWatch.episodes')} ({items.length}{items.length > 20 ? ` — top 20` : ''})</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {selectedForStt.size > 0 && (
                  <button onClick={handleStt} className="action-btn" style={{ fontSize: 12, padding: '3px 10px' }}>
                    {t('techWatch.transcribe')} ({selectedForStt.size})
                  </button>
                )}
              </div>
            </div>
            <div style={{ fontSize: 13 }}>
              {top20.map(item => (
                <div key={item.id} style={{ borderBottom: '1px solid #f3f4f6', padding: '8px 0', display: 'flex', gap: 6 }}>
                  {/* KB / STT checkboxes */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center', paddingTop: 2 }}>
                    <div title={t('techWatch.selectForKb')}>
                      {!item.user_selected && item.full_text_status === 'not_downloaded' ? (
                        <input type="checkbox" checked={selectedForKb.has(item.id)} onChange={() => toggleKb(item.id)} />
                      ) : fullTextIcon(item.full_text_status)}
                    </div>
                    {item.stt_status === 'none' && item.audio_url && (
                      <input type="checkbox" title="STT" checked={selectedForStt.has(item.id)} onChange={() => toggleStt(item.id)} />
                    )}
                  </div>
                  {/* Main content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      {item.technical_field && (
                        <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {item.technical_field}
                        </span>
                      )}
                      <span style={{ fontWeight: 500, flex: 1, minWidth: 0 }}>
                        {item.item_url ? (
                          <a href={item.item_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', textDecoration: 'none' }}>
                            {item.digest_title || item.item_title}
                          </a>
                        ) : (item.digest_title || item.item_title)}
                        {sourceTypeBadge(item.source_type)}
                      </span>
                      <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {item.source_label}{item.item_date ? ` · ${item.item_date}` : ''}
                        {item.authors ? ` · ${item.authors}` : ''}
                      </span>
                      {item.relevance_score != null && (
                        <span style={{ fontWeight: 600, color: relevanceColor(item.relevance_score), whiteSpace: 'nowrap' }}>
                          {item.relevance_score}/5
                        </span>
                      )}
                    </div>
                    {item.summary && (
                      <div style={{ color: '#374151', fontSize: 12, marginTop: 3 }}>{item.summary}</div>
                    )}
                    {item.relevance_note && (
                      <div style={{ marginTop: 4, padding: '3px 8px', background: '#fefce8', borderRadius: 4, fontSize: 12, color: '#854d0e', borderLeft: '3px solid #ca8a04' }}>
                        <strong>{t('techWatch.relevanceNote')}: </strong>{item.relevance_note}
                      </div>
                    )}
                    {item.deep_summary && (
                      <div style={{ marginTop: 4, padding: '3px 8px', background: '#f0fdf4', borderRadius: 4, fontSize: 12, color: '#166534' }}>
                        <strong>{t('techWatch.deepSummary')}:</strong> {item.deep_summary}
                      </div>
                    )}
                    {item.problem_relationship && (
                      <div style={{ marginTop: 2, padding: '3px 8px', background: '#eff6ff', borderRadius: 4, fontSize: 12, color: '#1e40af' }}>
                        <strong>{t('techWatch.problemRelationship')}:</strong> {item.problem_relationship}
                      </div>
                    )}
                    {(item.stt_status === 'done' || item.stt_status === 'failed' || item.stt_status === 'transcribing' || item.stt_status === 'requested' || item.full_text_status === 'downloading') && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                        {item.stt_status === 'done' && t('techWatch.sttDone')}
                        {item.stt_status === 'failed' && <span style={{ color: '#dc2626' }}>{t('techWatch.sttFailed')}</span>}
                        {(item.stt_status === 'transcribing' || item.stt_status === 'requested') && t('techWatch.transcribing')}
                        {item.full_text_status === 'downloading' && <span style={{ color: '#ca8a04' }}>{t('techWatch.acquiring')}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Screening Report */}
            {(() => {
              const sourceMap = new Map<string, { source_type: string; source_label: string; count: number }>();
              for (const item of items) {
                const entry = sourceMap.get(item.source_id);
                if (entry) { entry.count++; }
                else { sourceMap.set(item.source_id, { source_type: item.source_type, source_label: item.source_label || item.source_type, count: 1 }); }
              }
              const rows = Array.from(sourceMap.values());
              return (
                <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>{t('techWatch.screeningReport')}</div>
                  {rows.map((row, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
                      <span style={{ display: 'inline-block', fontSize: 10, background: row.source_type === 'arxiv' ? '#ede9fe' : row.source_type === 'openalex' ? '#e0f2fe' : '#f0fdf4', color: row.source_type === 'arxiv' ? '#7c3aed' : row.source_type === 'openalex' ? '#0369a1' : '#15803d', padding: '1px 5px', borderRadius: 3, marginRight: 6 }}>{row.source_type === 'openalex' ? 'OpenAlex' : row.source_type}</span>
                      <span style={{ marginRight: 6 }}>{row.source_label}</span>
                      <span style={{ color: '#6b7280' }}>— {row.count} {t('techWatch.screeningCount').toLowerCase()}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        );
      })()}
    </div>
  );
}

